import React from "react";
import {ConfigV1alpha1Template} from "../../../../../../../gen/models/configV1alpha1Template";
import {ErrorTypeNotFound, Result, ResultError, Return} from "../../../../../../lib/result";
import {SectionProps} from "../../../../../../components/Drawer/ItemDrawer";
import Section from "../../../../../../components/Drawer/Section/Section";
import Label from "../../../../../../components/Label/Label";
import Description from "../../../../../../components/Description/Description";
import * as yaml from "js-yaml";
import {arr, selectOwnerFilter} from "../../../../../../lib/helpers/renderhelper";
import TextArea from "../../../../../../components/TextArea/TextArea";
import Button from "../../../../../../components/Button/Button";
import styles from "./TemplateSpec.module.scss";
import {PlusOutlined} from "@ant-design/icons/lib";
import Tabs from "../../../../../../components/Tabs/Tabs";
import Input from "../../../../../../components/Input/Input";
import {ConfigV1alpha1HelmSecretRef} from "../../../../../../../gen/models/configV1alpha1HelmSecretRef";
import {NewResource, Resources} from "../../../../../../lib/resources";
import client, {GroupVersionResource} from "../../../../../../lib/client";
import {randomString} from "../../../../../../lib/helper";
import Query from "../../../../../../components/Query/Query";
import {ErrorMessage} from "../../../../../../components/ErrorMessage/ErrorMessage";
import Loading from "../../../../../../components/Loading/Loading";
import Select from "../../../../../../components/Select/Select";
import constants from "../../../../../../constants/constants";
import {V1ObjectMeta} from "@kubernetes/client-node";
import {V1OwnerReference} from "../../../../../../../gen/models/V1OwnerReference";
import {addOwner, newOwnerReference} from "../../../../../../lib/helpers/kubehelper";
import YAMLEditor from "../../../../../../components/YAMLEditor/YAMLEditor";
import Checkbox from "../../../../../../components/Checkbox/Checkbox";
const { TabPane } = Tabs;
const { Option } = Select;

interface TemplateSpecState {
    manifests: string;
    manifestUrls: string[];

    release: string | undefined;
    chartName: string | undefined;
    chartVersion: string | undefined;
    chartRepo: string | undefined;
    chartRepoUsername: string | undefined;
    chartRepoPassword: string | undefined;
    chartValues: string | undefined;
    chartInsecure: boolean | undefined;

    usernameSecret: ConfigV1alpha1HelmSecretRef | undefined;
    passwordSecret: ConfigV1alpha1HelmSecretRef | undefined;

    // this is a workaround, because the logic of the antd select does not really allow
    // to prevent selection of items except making them disabled (which is not desired in this case)
    // so we will quickly select and deselect
    copyFromTemplate: string | undefined;
}

interface TemplateSpecProps extends SectionProps {
    template?: ConfigV1alpha1Template;

    cluster: string;
    allTemplates: ConfigV1alpha1Template[];
}

interface RepositoryCredentials {
    username?: string | undefined;
    password?: string | undefined;
}

async function getValueBySecret(cluster: string, secretRef: ConfigV1alpha1HelmSecretRef): Promise<Result<string>> {
    const getSecretResult = await client.cluster(cluster, Resources.V1Secret).Namespace(secretRef.namespace).Get(secretRef.name);
    if (getSecretResult.err) {
        return getSecretResult;
    }

    const secret = getSecretResult.val;
    if (!secret.data) {
        return Return.Failed(`couldn't retrieve value from secret ${secretRef.namespace}/${secretRef.name}, because the secret has no data`);
    }

    let key = secretRef.key;
    if (!key) {
        const keys = Object.keys(secret.data);
        if (keys.length === 0) {
            return Return.Failed(`couldn't retrieve value from secret ${secretRef.namespace}/${secretRef.name}, because the secret has no data`);
        } else if (keys.length > 1) {
            return Return.Failed(`couldn't retrieve value from secret ${secretRef.namespace}/${secretRef.name}, because no key is specified but the secret has multiple`);
        }

        key = keys[0];
    }

    if (!secret.data[key]) {
        return Return.Failed(`couldn't retrieve value from secret ${secretRef.namespace}/${secretRef.name}, because key ${key} has no data`);
    }

    return Return.Value(atob(secret.data[key]));
}

async function setValueBySecret(cluster: string, owner: V1OwnerReference, secretRef: ConfigV1alpha1HelmSecretRef, value: string): Promise<ResultError> {
    const getSecretResult = await client.cluster(cluster, Resources.V1Secret).Namespace(secretRef.namespace).Get(secretRef.name);
    if (getSecretResult.err) {
        if (getSecretResult.val.type === ErrorTypeNotFound) {
            // check if we have to create the namespace
            const getNamespaceResult = await client.cluster(cluster, Resources.V1Namespace).Get(secretRef.namespace);
            if (getNamespaceResult.err) {
                if (getNamespaceResult.val.type !== ErrorTypeNotFound) {
                    return getNamespaceResult;
                }

                const createNamespaceResult = await client.cluster(cluster, Resources.V1Namespace).Create(NewResource(Resources.V1Namespace, secretRef.namespace))
                if (createNamespaceResult.err) {
                    return createNamespaceResult;
                }
            }

            const resource = NewResource(Resources.V1Secret, secretRef.name, {
                type: "Opaque",
                data: {
                    [secretRef.key]: btoa(value)
                }
            });
            addOwner(resource.metadata!, owner);

            const createResult = await client.cluster(cluster, Resources.V1Secret).Namespace(secretRef.namespace).Create(resource);
            if (createResult.err) {
                return createResult;
            }
            return Return.Ok();
        }

        return getSecretResult;
    }

    const secret = getSecretResult.val;
    let key = secretRef.key;
    if (!key) {
        if (!secret.data) {
            return Return.Failed(`couldn't retrieve value from secret ${secretRef.namespace}/${secretRef.name}, because the secret has no data and no key is specified`);
        }

        const keys = Object.keys(secret.data);
        if (keys.length === 0) {
            return Return.Failed(`couldn't set value to secret ${secretRef.namespace}/${secretRef.name}, because the secret has no data and no key specified`);
        } else if (keys.length > 1) {
            return Return.Failed(`couldn't set value to secret ${secretRef.namespace}/${secretRef.name}, because no key is specified but the secret has multiple`);
        }

        key = keys[0];
    }

    if (!secret.data) {
        secret.data = {};
    }

    secret.data[key] = btoa(value);

    // save secret
    const updateSecretResult = await client.cluster(cluster, Resources.V1Secret).Namespace(secretRef.namespace).Update(secretRef.name, secret);
    if (updateSecretResult.err) {
        return updateSecretResult;
    }

    return Return.Ok();
}

async function generateSecretName<T>(cluster: string, prefix: string, namespace: string, resource: GroupVersionResource<T>): Promise<Result<string>> {
    let name = prefix;
    while(true) {
        let result = await client.cluster(cluster, resource).Namespace(namespace).Get(name);
        if (result.err) {
            if (result.val.type === ErrorTypeNotFound) {
                return Return.Value(name);
            }

            return result;
        }

        name = prefix + "-" + randomString(4);
    }
}

async function newSecretRef(cluster: string, metadata: V1ObjectMeta): Promise<Result<ConfigV1alpha1HelmSecretRef>> {
    const generateResult = await generateSecretName(cluster, metadata.name! ? metadata.name! : metadata.generateName + "t", constants.LoftDataNamespace, Resources.V1Secret);
    if (generateResult.err) {
        return generateResult;
    }

    return Return.Value({
        name: generateResult.val,
        namespace: constants.LoftDataNamespace,
        key: ""
    });
}

export default class TemplateSpec extends React.PureComponent<TemplateSpecProps, TemplateSpecState> {
    state: TemplateSpecState = {
        manifests: this.props.template?.resources?.manifests?.length ? arr(this.props.template?.resources?.manifests).map(manifest => yaml.dump(manifest)).join("\n---\n") : "",
        manifestUrls: this.props.template?.resources?.remoteManifests?.length ? arr(this.props.template?.resources?.remoteManifests).map(manifest => manifest.url + "") : [""],

        release: this.props.template?.resources?.helm?.releaseName,
        chartName: this.props.template?.resources?.helm?.chart?.repository?.name,
        chartVersion: this.props.template?.resources?.helm?.chart?.repository?.version,
        chartRepo: this.props.template?.resources?.helm?.chart?.repository?.repoUrl,
        chartInsecure: !!this.props.template?.resources?.helm?.chart?.repository?.insecure,
        chartRepoUsername: undefined,
        chartRepoPassword: undefined,
        chartValues: this.props.template?.resources?.helm?.values,

        usernameSecret: this.props.template?.resources?.helm?.chart?.repository?.username,
        passwordSecret: this.props.template?.resources?.helm?.chart?.repository?.password,

        copyFromTemplate: undefined
    };

    create = async (template: ConfigV1alpha1Template): Promise<ResultError> => {
        return this.update(template);
    };

    update = async (template: ConfigV1alpha1Template): Promise<ResultError> => {
        if (!template.resources) {
            template.resources = {};
        }

        // manifests
        const filteredManifests = this.state.manifests.split("\n---\n");
        const parsedManifests: object[] = [];
        for (let i = 0; i < filteredManifests.length; i++) {
            try {
                const parsed = yaml.load(filteredManifests[i]) as object;
                if (parsed) {
                    parsedManifests.push(parsed);
                }
            } catch(e: any) {
                return Return.Failed(`Error parsing ${filteredManifests[i]}: ${e.message}`);
            }
        }
        template.resources.manifests = parsedManifests;
        template.resources.remoteManifests = this.state.manifestUrls.filter(m => !!m).map(m => ({
            url: m,
            insecure: true
        }));

        // helm
        if (this.state.chartRepo && this.state.chartName) {
            let release = this.state.release ? this.state.release : this.state.chartName;
            if (!template.resources.helm) {
                template.resources.helm = {};
            }

            template.resources.helm.releaseName = release;
            template.resources.helm.values = this.state.chartValues;

            if (!template.resources.helm.chart) {
                template.resources.helm.chart = {};
            }
            if (!template.resources.helm.chart.repository) {
                template.resources.helm.chart.repository = {
                    name: this.state.chartName
                };
            }
            template.resources.helm.chart.repository.name = this.state.chartName;
            template.resources.helm.chart.repository.repoUrl = this.state.chartRepo;
            template.resources.helm.chart.repository.version = this.state.chartVersion;
            template.resources.helm.chart.repository.insecure = this.state.chartInsecure;

            let secretRef: ConfigV1alpha1HelmSecretRef | undefined = undefined;
            if (this.state.chartRepoUsername) {
                if (!template.resources.helm.chart.repository.username) {
                    const secretRefResult = await newSecretRef(this.props.cluster, template.metadata!);
                    if (secretRefResult.err) {
                        return secretRefResult;
                    }

                    secretRef = secretRefResult.val;
                    template.resources.helm.chart.repository.username = {...secretRef, key: "username"};
                }
            } else {
                template.resources.helm.chart.repository.username = undefined;
            }

            if (this.state.chartRepoPassword) {
                if (!template.resources.helm.chart.repository.password) {
                    if (!secretRef) {
                        const secretRefResult = await newSecretRef(this.props.cluster, template.metadata!);
                        if (secretRefResult.err) {
                            return secretRefResult;
                        }

                        secretRef = secretRefResult.val;
                    }

                    template.resources.helm.chart.repository.password = {...secretRef, key: "password"};
                }
            } else {
                template.resources.helm.chart.repository.password = undefined;
            }
        } else {
            template.resources.helm = undefined;
        }

        return Return.Ok();
    };

    updateSecrets = async (template: ConfigV1alpha1Template): Promise<ResultError> => {
        const ownerReference = newOwnerReference(template.apiVersion!, template.kind!, template.metadata!);
        if (this.state.chartRepoUsername) {
            const result = await setValueBySecret(this.props.cluster, ownerReference, template.resources!.helm!.chart!.repository!.username!, this.state.chartRepoUsername);
            if (result.err) {
                return result;
            }
        }

        if (this.state.chartRepoPassword) {
            const result = await setValueBySecret(this.props.cluster, ownerReference, template.resources!.helm!.chart!.repository!.password!, this.state.chartRepoPassword);
            if (result.err) {
                return result;
            }
        }

        return Return.Ok();
    };

    render() {
        if (this.props.mode === "batch") {
            return null;
        }

        return <Section title={`Template`}>
            <Query query={async (): Promise<ResultError> => {
                const credentials: RepositoryCredentials = {};
                if (this.state.usernameSecret) {
                    const result = await getValueBySecret(this.props.cluster, this.state.usernameSecret);
                    if (result.err) {
                        console.error(result.val);
                    } else {
                        credentials.username = result.val;
                    }
                }
                if (this.state.passwordSecret) {
                    const result = await getValueBySecret(this.props.cluster, this.state.passwordSecret);
                    if (result.err) {
                        console.error(result.val);
                    } else {
                        credentials.password = result.val;
                    }
                }

                // update the state when we have the values
                this.setState({
                    chartRepoUsername: credentials.username,
                    chartRepoPassword: credentials.password,
                });

                return Return.Ok();
            }} skip={!this.state.usernameSecret && !this.state.passwordSecret}>
                {
                    queryResult => {
                        if (queryResult.error) {
                            return <ErrorMessage error={queryResult.error} />;
                        } else if (queryResult.loading) {
                            return <Loading />;
                        }

                        return <React.Fragment>
                            <Label>Copy Other Template</Label>
                            <Select
                                showSearch
                                allowClear
                                placeholder="Select Template"
                                style={{ width: '100%' }}
                                value={this.state.copyFromTemplate}
                                onChange={value => {
                                    const template = this.props.allTemplates.find(template => template.metadata?.name === value);
                                    if (template) {
                                        // update state
                                        this.setState({
                                            manifests: template.resources?.manifests?.length ? arr(template.resources?.manifests).map(manifest => yaml.dump(manifest)).join("\n---\n") : "",
                                            manifestUrls: template.resources?.remoteManifests?.length ? arr(template.resources?.remoteManifests).map(manifest => manifest.url + "") : [""],

                                            release: template.resources?.helm?.releaseName,
                                            chartName: template.resources?.helm?.chart?.repository?.name,
                                            chartVersion: template.resources?.helm?.chart?.repository?.version,
                                            chartRepo: template.resources?.helm?.chart?.repository?.repoUrl,
                                            chartInsecure: template.resources?.helm?.chart?.repository?.insecure,
                                            chartRepoUsername: undefined,
                                            chartRepoPassword: undefined,
                                            chartValues: template.resources?.helm?.values,
                                            usernameSecret: template.resources?.helm?.chart?.repository?.username,
                                            passwordSecret: template.resources?.helm?.chart?.repository?.password,

                                            copyFromTemplate: ""
                                        }, async () => {
                                            // refetch secret values here
                                            await queryResult.refetch();

                                            // deselect the select
                                            this.setState({copyFromTemplate: undefined});
                                        });
                                    }
                                }}
                                optionFilterProp="children"
                                filterOption={selectOwnerFilter}
                            >
                                {this.props.allTemplates.map(template => {
                                    return <Option key={template.metadata?.name} value={template.metadata?.name!}>
                                        {template.metadata?.name!}
                                    </Option>
                                })}
                            </Select>
                            <Description>Selecting a template will copy the values for its definition into the field below.</Description>
                            <Tabs defaultActiveKey="1" type="card" size={"small"}>
                                <TabPane tab="kubectl" key="1">
                                    <Label>Kubernetes Manifests</Label>
                                    <YAMLEditor value={this.state.manifests} 
                                                minLines={10}
                                                maxLines={100}
                                                onChange={val => {
                                        this.setState({manifests: val});
                                    }} placeholder={`apiVersion: v1
kind: LimitRange
...`} />
                                    <Description>Each manifest will be created when this template is applied to a space or virtual cluster. For more information please take a look at the <a href={"https://loft.sh/docs"} target={"_blank"}>loft documenation</a>.</Description>
                                </TabPane>
                                <TabPane tab="manifest urls" key="2">
                                    {this.state.manifestUrls.map((content, index) => <div>
                                        <Input key={index} value={content} className={styles["manifest-url"]} onChange={e => {
                                            const newArray = [...this.state.manifestUrls];
                                            newArray[index] = e.target.value;
                                            this.setState({manifestUrls: newArray});
                                        }} placeholder="https://github.com/kubernetes-sigs/metrics-server/releases/latest/download/components.yaml" />
                                    </div>)}
                                    <Button type={"primary"} onClick={() => this.setState({manifestUrls: [...this.state.manifestUrls, ""]})}><PlusOutlined />Manifest Url</Button>
                                    <Description>All manifests found in the target location will be deployed when this template is applied to a space or virtual cluster. For more information please take a look at the <a href={"https://loft.sh/docs"} target={"_blank"}>loft documenation</a>.</Description>
                                </TabPane>
                                <TabPane tab="helm" key="3">
                                    <Description>Loft will execute helm template for this chart and deploy all manifests found when this template is applied to a space or virtual cluster. For more information please take a look at the <a href={"https://loft.sh/docs"} target={"_blank"}>loft documenation</a></Description>
                                    <Label>Release Name</Label>
                                    <Input placeholder={"Leave empty for auto-generated release name"} value={this.state.release} onChange={e => this.setState({release: e.target.value})} />
                                    <div className={styles["row"]}>
                                        <div>
                                            <Label>Chart Name</Label>
                                            <Input placeholder={"mysql"} value={this.state.chartName} onChange={e => this.setState({chartName: e.target.value})} />
                                        </div>
                                        <div>
                                            <Label>Chart Version</Label>
                                            <Input placeholder={"4.5.1"} value={this.state.chartVersion} onChange={e => this.setState({chartVersion: e.target.value})} />
                                        </div>
                                    </div>
                                    <Label>Chart Repository URL</Label>
                                    <Input placeholder={"https://chart.mydomain.tld"} value={this.state.chartRepo} onChange={e => this.setState({chartRepo: e.target.value})} />
                                    <div className={styles["row"]}>
                                        <div>
                                            <Label>Chart Repository Username</Label>
                                            <Input placeholder={"repo-username"} value={this.state.chartRepoUsername} onChange={e => this.setState({chartRepoUsername: e.target.value})} />
                                        </div>
                                        <div>
                                            <Label>Chart Repository Password</Label>
                                            <Input type={"password"} placeholder={"****************"} value={this.state.chartRepoPassword} onChange={e => this.setState({chartRepoPassword: e.target.value})} />
                                        </div>
                                    </div>
                                    <Label>Chart Values</Label>
                                    <TextArea placeholder={`ingress:
  enabled: false
...`} value={this.state.chartValues} onChange={e => this.setState({chartValues: e.target.value})} />
                                    <Label>
                                        Insecure Skip TLS Verify
                                    </Label>
                                    <Checkbox checked={this.state.chartInsecure}
                                              onChange={e => this.setState({chartInsecure: e.target.checked})}>
                                        Skip tls certificate checks for chart download
                                    </Checkbox>
                                </TabPane>
                            </Tabs>
                        </React.Fragment>
                    }
                }
            </Query>
        </Section>
    }
}